import { useState, useCallback, useEffect } from 'react'

type Coordinates = {
	lat: number
	lng: number
}

type GeolocationState = {
	coordinates: Coordinates | null
	error: GeolocationPositionError | null
	loading: boolean
}

const STORAGE_KEY = 'gto_user_location'
const LOCATION_MAX_AGE_MS = 30 * 60 * 1000 // 30 minutes

type StoredLocation = {
	lat: number
	lng: number
	timestamp: number
}

function getStoredLocation(): Coordinates | null {
	if (typeof window === 'undefined') return null
	try {
		const stored = sessionStorage.getItem(STORAGE_KEY)
		if (!stored) return null
		const parsed = JSON.parse(stored) as StoredLocation
		// Check if location is still fresh (within 30 minutes)
		if (Date.now() - parsed.timestamp < LOCATION_MAX_AGE_MS) {
			return { lat: parsed.lat, lng: parsed.lng }
		}
		// Expired, remove it
		sessionStorage.removeItem(STORAGE_KEY)
		return null
	} catch {
		return null
	}
}

function storeLocation(coords: Coordinates): void {
	if (typeof window === 'undefined') return
	try {
		const toStore: StoredLocation = {
			lat: coords.lat,
			lng: coords.lng,
			timestamp: Date.now(),
		}
		sessionStorage.setItem(STORAGE_KEY, JSON.stringify(toStore))
	} catch {
		// Ignore storage errors
	}
}

export function useGeolocation() {
	const [state, setState] = useState<GeolocationState>({
		coordinates: null,
		error: null,
		loading: false,
	})

	// On mount, check for stored location
	useEffect(() => {
		const stored = getStoredLocation()
		if (stored) {
			setState((prev) => ({ ...prev, coordinates: stored }))
		}
	}, [])

	const requestLocation = useCallback(() => {
		// First check if we have a recent stored location
		const stored = getStoredLocation()
		if (stored) {
			setState({
				coordinates: stored,
				error: null,
				loading: false,
			})
			return
		}

		if (!navigator.geolocation) {
			setState((prev) => ({
				...prev,
				error: {
					code: 2,
					message: 'Geolocation not supported',
					PERMISSION_DENIED: 1,
					POSITION_UNAVAILABLE: 2,
					TIMEOUT: 3,
				} as GeolocationPositionError,
			}))
			return
		}

		setState((prev) => ({ ...prev, loading: true, error: null }))

		navigator.geolocation.getCurrentPosition(
			(position) => {
				const coords = {
					lat: position.coords.latitude,
					lng: position.coords.longitude,
				}
				// Store for future use
				storeLocation(coords)
				setState({
					coordinates: coords,
					error: null,
					loading: false,
				})
			},
			(error) => {
				setState({ coordinates: null, error, loading: false })
			},
			{ enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
		)
	}, [])

	return { ...state, requestLocation }
}

// Utility to get stored location without triggering a request
export function getPersistedLocation(): Coordinates | null {
	return getStoredLocation()
}
